import React, { useState, useEffect, useRef } from 'react';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { useNavigate } from 'react-router-dom';
import Footer from '../components/common/Footer';
import toast from 'react-hot-toast';
import { useCreateSolarLeadMutation } from '../store/slices/apiSlice';
import {
    CheckCircle,
    ArrowRight,
    Play,
    Pause,
    Zap,
    Battery,
    Home,
    Car,
    Settings,
    Eye,
    Bell,
    ChevronLeft,
    ChevronRight,
    MapPin
} from 'lucide-react';
import { API_BASE_URL } from '../config/api';


const Solar = () => {
    const navigate = useNavigate();
    const [isVisible, setIsVisible] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        location: '',
        country: '',
        state: '',
        local_government_area: '',
        propertyType: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isVideoPlaying, setIsVideoPlaying] = useState(true);
    const [activeSlide, setActiveSlide] = useState(0);
    const locationInputRef = useRef<HTMLInputElement>(null);
    const [locationSuggestions, setLocationSuggestions] = useState<
        { place_id: string; description: string; main_text?: string; secondary_text?: string }[]
    >([]);
    const [isLocationLoading, setIsLocationLoading] = useState(false);
    const [locationError, setLocationError] = useState<string | null>(null);

    const [createSolarLead] = useCreateSolarLeadMutation();

    // Solar images array
    const solarImages = [
        '/solar 1.avif',
        '/solar 2.avif',
        '/solar 3.avif',
        '/solar 4.avif',
        '/solar 5.avif',
        '/solar 6.avif'
    ];

    usePageMetadata('solar');

    useEffect(() => {
        setIsVisible(true);
    }, []);

    // Auto-rotate carousel with smooth transitions
    useEffect(() => {
        if (!isVideoPlaying) return;

        const interval = setInterval(() => {
            setActiveSlide((prev) => (prev + 1) % solarImages.length);
        }, 4000); // Change slide every 4 seconds

        return () => clearInterval(interval);
    }, [isVideoPlaying, solarImages.length]);


    // Fetch location suggestions from backend (Google Places proxy)
    useEffect(() => {
        const fetchSuggestions = async () => {
            const query = formData.location.trim();
            if (query.length < 2) {
                setLocationSuggestions([]);
                return;
            }
            setIsLocationLoading(true);
            setLocationError(null);
            try {
                const url = new URL('/api/location/search', API_BASE_URL);
                url.searchParams.set('query', query);
                url.searchParams.set('limit', '5');

                const response = await fetch(url.toString());

                if (!response.ok) {
                    const text = await response.text();
                    throw new Error(text || 'Failed to fetch location suggestions');
                }

                const result = await response.json();

                if (!response.ok || !result?.success) {
                    throw new Error(result?.message || 'Failed to fetch location suggestions');
                }

                setLocationSuggestions(result.data?.predictions || []);
            } catch (error: any) {
                console.error('Location suggestions error:', error);
                setLocationError('Location suggestions unavailable. Please type full address.');
            } finally {
                setIsLocationLoading(false);
            }
        };

        const debounce = setTimeout(fetchSuggestions, 300);
        return () => clearTimeout(debounce);
    }, [formData.location, API_BASE_URL]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLocationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData(prev => ({ ...prev, location: e.target.value }));
        setLocationError(null);
    };

    const handleLocationSelect = async (suggestion: { place_id: string; description: string }) => {
        try {
            setIsLocationLoading(true);
            setLocationError(null);

            const url = new URL(`/api/location/details/${suggestion.place_id}`, API_BASE_URL);
            const response = await fetch(url.toString());

            if (!response.ok) {
                const text = await response.text();
                throw new Error(text || 'Failed to fetch location details');
            }

            const result = await response.json();

            if (!response.ok || !result?.success) {
                throw new Error(result?.message || 'Failed to fetch location details');
            }

            const addressString = result.data?.address || suggestion.description || '';
            // Best-effort parsing: split by comma
            const parts = addressString.split(',').map((p: string) => p.trim()).filter(Boolean);
            const country = parts[parts.length - 1] || '';
            const state = parts.length > 1 ? parts[parts.length - 2] : '';
            const lga = parts.length > 2 ? parts[parts.length - 3] : '';

            setFormData(prev => ({
                ...prev,
                location: addressString,
                country,
                state,
                local_government_area: lga
            }));
            setLocationSuggestions([]);
        } catch (error: any) {
            console.error('Location details error:', error);
            setLocationError('Could not fetch location details. Please type your full address.');
        } finally {
            setIsLocationLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);

        try {
            // Validate location data
            if (!formData.location || !formData.country || !formData.state || !formData.local_government_area) {
                toast.error('Please select a valid location from the autocomplete suggestions.');
                setIsSubmitting(false);
                return;
            }

            // Convert property type to lowercase and handle empty values
            const propertyType = formData.propertyType
                ? (formData.propertyType.toLowerCase() as 'residential' | 'commercial' | 'industrial' | 'agricultural' | 'other')
                : null;

            const response = await createSolarLead({
                name: formData.name.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                country: formData.country.trim(),
                state: formData.state.trim(),
                local_government_area: formData.local_government_area.trim(),
                property_type: propertyType,
                message: formData.message?.trim() || null
            }).unwrap();

            if (response.success) {
                toast.success(response.message || 'Thank you! We\'ll contact you soon to schedule your consultation.');
                navigate('/thank-you');
            } else {
                toast.error(response.message || 'Failed to submit. Please try again.');
            }
        } catch (error: any) {
            console.error('Solar lead submission error:', error);
            // Handle validation errors from API
            if (error?.data?.message) {
                toast.error(error.data.message);
            } else if (error?.data?.error) {
                toast.error(error.data.error);
            } else {
                toast.error('Failed to submit. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <div className="min-h-screen bg-black text-white">
            {/* Custom Navigation Bar */}
            <nav className="bg-black border-b border-gray-800 sticky top-0 z-50">
                <div className="container mx-auto max-w-7xl px-6 py-4 flex items-center justify-between">
                    <h2 className="text-lg font-normal text-white">DiasporaWise Energy Solutions</h2>
                    <div className="flex items-center gap-8">
                        <button
                            onClick={() => {
                                document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="text-white hover:text-gray-300 transition-colors text-sm font-normal"
                        >
                            Schedule a Consultation
                        </button>

                        <button
                            onClick={() => {
                                document.getElementById('signup-form')?.scrollIntoView({ behavior: 'smooth' });
                            }}
                            className="text-white hover:text-gray-300 transition-colors text-sm font-normal"
                        >
                            Get Started
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
                <div className="absolute inset-0 z-0">
                    {/* Image Slideshow */}
                    <div className="relative w-full h-full">
                        {solarImages.map((image, index) => (
                            <div
                                key={index}
                                className={`absolute inset-0 ${activeSlide === index
                                    ? 'opacity-100 scale-100'
                                    : 'opacity-0 scale-110'
                                    }`}
                                style={{
                                    transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
                                    zIndex: activeSlide === index ? 1 : 0,
                                    willChange: 'opacity, transform'
                                }}
                            >
                                <img
                                    src={image}
                                    alt={`Solar Power ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    loading={index === 0 ? 'eager' : 'lazy'}
                                />
                            </div>
                        ))}
                    </div>
                    <div className="absolute inset-0 bg-black/40 z-10"></div>
                </div>

                {/* Navigation Arrows */}
                <button
                    onClick={() => {
                        setActiveSlide((prev) => (prev - 1 + solarImages.length) % solarImages.length);
                    }}
                    className="absolute left-8 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 p-3 rounded-full transition-colors"
                    aria-label="Previous slide"
                >
                    <ChevronLeft className="w-6 h-6 text-white" />
                </button>
                <button
                    onClick={() => {
                        setActiveSlide((prev) => (prev + 1) % solarImages.length);
                    }}
                    className="absolute right-8 top-1/2 transform -translate-y-1/2 z-30 bg-black/60 hover:bg-black/80 p-3 rounded-full transition-colors"
                    aria-label="Next slide"
                >
                    <ChevronRight className="w-6 h-6 text-white" />
                </button>

                {/* Play/Pause Control */}
                <button
                    onClick={() => setIsVideoPlaying(!isVideoPlaying)}
                    className="absolute bottom-8 left-8 z-30 bg-black/60 hover:bg-black/80 p-3 rounded transition-colors"
                    aria-label={isVideoPlaying ? 'Pause slideshow' : 'Play slideshow'}
                >
                    {isVideoPlaying ? (
                        <Pause className="w-5 h-5 text-white" />
                    ) : (
                        <Play className="w-5 h-5 text-white" />
                    )}
                </button>

                {/* Carousel Indicators */}
                <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-30 flex gap-2">
                    {solarImages.map((_, index) => (
                        <button
                            key={index}
                            onClick={() => {
                                setActiveSlide(index);
                            }}
                            className={`rounded-full transition-all duration-300 ${activeSlide === index
                                ? 'bg-white h-2 w-8'
                                : 'bg-white/50 hover:bg-white/75 h-2 w-2'
                                }`}
                            aria-label={`Go to slide ${index + 1}`}
                        />
                    ))}
                </div>

                {/* Hero Content */}
                <div className="relative z-10 container mx-auto px-6 max-w-7xl">
                    <div className={`text-white transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                        <h1 className="text-5xl md:text-7xl lg:text-8xl font-normal mb-6 tracking-tight max-w-4xl">
                            Sustainable Energy
                        </h1>
                        <p className="text-xl md:text-2xl mb-8 text-gray-300 font-light max-w-3xl">
                            Reliable Power, Endless Possibilities
                        </p>
                    </div>
                </div>
            </section>

            {/* Save On Electricity Bills Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                            Energy Independence Starts Here
                        </h2>
                        <p className="text-xl text-gray-400 max-w-4xl mx-auto leading-relaxed">
                            Harness the sun's power to eliminate energy uncertainty. Our integrated solar and storage solutions provide 24/7 reliability, reducing your reliance on an unstable grid while slashing your monthly costs. Invest in a future where you control your own power.
                        </p>
                    </div>
                </div>
            </section>

            {/* Benefits Section - Three Columns */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        <div>
                            <h3 className="text-2xl font-normal mb-4 text-white">Eco-Friendly Power</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Power your home with emissions-free, renewable energy directly from the sun.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-normal mb-4 text-white">Investment Value</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Our systems are designed for high ROI, often paying for themselves through energy savings and increased property value.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-normal mb-4 text-white">Total Reliability</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Protect your home or business from rising utility costs and frequent outages. With our advanced storage, you have power whenever you need it most.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Install Solar Power, Power a Sustainable Home Section */}
            <section className="py-24 px-6 bg-black relative min-h-[80vh] flex items-center">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                        alt="Solar installation"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>
                <div className="relative z-10 container mx-auto max-w-7xl px-6">
                    <div className="max-w-4xl">
                        <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                            Smart Solar Integration for Modern Living
                        </h2>
                        <p className="text-xl text-gray-300 leading-relaxed mb-12">
                            Experience a fully integrated energy ecosystem. Our premium panels and smart storage units work in harmony to optimize your energy consumption, ensuring you never waste a single kilowatt of sunshine.
                        </p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div className="flex items-start gap-4">
                                <Zap className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-white">Generate high-efficiency energy from the sun, even in low-light conditions.</h4>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Home className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-white">Seamlessly power your entire home or facility with clean, stable electricity.</h4>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Battery className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-white">Store surplus power in our high-capacity battery units for nighttime use or backup during grid failures.</h4>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Car className="w-6 h-6 text-white flex-shrink-0 mt-1" />
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-white">Integrate with electric vehicle charging stations for a truly carbon-neutral transportation experience.</h4>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Store Any Extra Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                                Advanced Battery Storage
                            </h2>
                            <p className="text-lg text-gray-400 leading-relaxed">
                                Our smart storage solutions capture every drop of sunshine. Whether it's for peak-shaving, nighttime usage, or emergency backup, our batteries provide the security you deserve.
                            </p>
                        </div>
                        <div className="relative min-h-[400px]">
                            <img
                                src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                                alt="Smart energy storage"
                                className="w-full h-full object-cover rounded-lg"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Sleek and Durable Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center mb-16">
                        <div>
                            <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                                Premium Grade Components
                            </h2>
                            <p className="text-lg text-gray-400 leading-relaxed mb-8">
                                We partner with leading manufacturers to provide tier-1 solar technology. Our mounting solutions are engineered for safety and aesthetic appeal, ensuring your system looks as good as it performs. Built to endure harsh weather conditions, these systems are a lifetime investment.
                            </p>
                        </div>
                        <div className="relative min-h-[400px]">
                            <img
                                src="https://images.unsplash.com/photo-1509391366360-2e959784a276?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                                alt="Solar power system on roof"
                                className="w-full h-full object-cover rounded-lg"
                            />
                        </div>
                    </div>

                    {/* Features Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <div>
                            <h3 className="text-xl font-normal mb-3 text-white">Premium Aesthetics</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                High-quality finishes that complement any architectural style without compromising on power.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-normal mb-3 text-white">Weather Resistant</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Certified to withstand high winds, heavy rain, and extreme heat environments.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-normal mb-3 text-white">Easy Installation</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Professional installation by certified engineers ensures minimal disruption to your property.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-xl font-normal mb-3 text-white">Low Profile</h3>
                            <p className="text-gray-400 text-sm leading-relaxed">
                                Advanced mounting systems keep a clean profile while maximizing airflow and efficiency.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Grid Goes Down, Lights Stay On Section */}
            <section className="py-24 px-6 bg-black relative min-h-[80vh] flex items-center">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                        alt="Powerwall during outage"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>
                <div className="relative z-10 container mx-auto max-w-7xl px-6">
                    <div className="max-w-3xl">
                        <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                            Zero Downtime, Total Control
                        </h2>
                        <p className="text-xl text-gray-300 leading-relaxed">
                            When the grid fails, your home doesn't have to. Our systems switch instantly to battery power, keeping your lights on, your internet connected, and your essential appliances running. Experience the peace of mind that comes with true energy self-sufficiency.
                        </p>
                    </div>
                </div>
            </section>

            {/* 24/7 Monitoring Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-normal mb-4 text-white">
                            Real-Time Energy Insights
                        </h2>
                        <p className="text-xl text-gray-400 max-w-3xl mx-auto">
                            Take command of your energy with the DiasporaWise app. Track production and consumption patterns to maximize your savings.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Settings className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-normal mb-3 text-white">Customize Preferences</h3>
                            <p className="text-gray-400">
                                Decide if you want to optimize your generated energy for savings, backup protection—or both.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Eye className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-normal mb-3 text-white">Monitor Energy Flow</h3>
                            <p className="text-gray-400">
                                See how much energy your system is generating, how it's being used and how much it's helping you save.
                            </p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Bell className="w-8 h-8 text-white" />
                            </div>
                            <h3 className="text-xl font-normal mb-3 text-white">Get Urgent Alerts</h3>
                            <p className="text-gray-400">
                                Get real-time notifications about system health and local grid status.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Getting to Power On Section */}
            <section className="py-24 px-6 bg-black relative min-h-[80vh] flex items-center">
                <div className="absolute inset-0 z-0">
                    <img
                        src="https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                        alt="Solar installation process"
                        className="w-full h-full object-cover opacity-30"
                    />
                    <div className="absolute inset-0 bg-black/60"></div>
                </div>
                <div className="relative z-10 container mx-auto max-w-7xl px-6">
                    <div className="max-w-3xl">
                        <h2 className="text-4xl md:text-5xl font-normal mb-6 text-white">
                            The Path to Energy Freedom
                        </h2>
                        <p className="text-xl text-gray-300 leading-relaxed mb-12">
                            Our experienced team manages the entire process for you—from initial site survey and design to permitting and final commissioning. Join thousands of satisfied homeowners who have made the switch.
                        </p>
                        <a
                            href="#signup-form"
                            className="text-blue-400 underline hover:text-blue-300 text-lg"
                        >
                            Schedule a digital consultation
                        </a>
                    </div>
                </div>
            </section>

            {/* Process Flow Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
                        <div>
                            <h3 className="text-xl font-normal mb-4 text-gray-400">Precision Design</h3>
                        </div>
                        <div>
                            <h3 className="text-xl font-normal mb-4 text-gray-400">Quality Assured Installation</h3>
                            <p className="text-gray-400 leading-relaxed">
                                We pride ourselves on rapid deployment and attention to detail. Our team handles all regulatory requirements and utility interconnections, ensuring your system is safe and fully compliant.
                            </p>
                        </div>
                        <div>
                            <h3 className="text-2xl font-normal mb-4 text-white">From Day One to Onward</h3>
                            <p className="text-gray-400 leading-relaxed">
                                Our commitment doesn't end at installation. We provide comprehensive warranties and ongoing support to ensure your system performs at its peak for decades.
                            </p>
                        </div>
                    </div>
                    <div className="relative min-h-[60vh]">
                        <img
                            src="https://images.unsplash.com/photo-1497436072909-60f360e1d4b1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
                            alt="Engineered for efficiency"
                            className="w-full h-full object-cover rounded-lg"
                        />
                    </div>
                </div>
            </section>

            {/* Technical Excellence Section */}
            <section className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-7xl">
                    <h2 className="text-4xl md:text-5xl font-normal mb-12 text-white">Technical Excellence</h2>

                    <div className="space-y-16">
                        {/* System Architecture */}
                        <div>
                            <h3 className="text-2xl font-normal mb-8 text-white">System Architecture</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Panel Geometry</h4>
                                    <p className="text-white">Precision-engineered high-density modules optimized for maximum roof coverage.</p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Build Quality</h4>
                                    <p className="text-white">Corrosion-resistant anodized frames with high-transparency tempered glass and ultra-pure monocrystalline cells.</p>
                                </div>
                            </div>
                        </div>

                        {/* Performance Metrics */}
                        <div>
                            <h3 className="text-2xl font-normal mb-8 text-white">Performance Metrics</h3>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Power Output</h4>
                                    <p className="text-white">High-yield output reaching up to 450W+ per module depending on configuration.</p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Thermal Resilience</h4>
                                    <p className="text-white">Operational efficiency maintained from -40°C to +85°C, perfect for tropical climates.</p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Safety Compliance</h4>
                                    <p className="text-white">Global safety certifications including IEC 61215, IEC 61730, and local regulatory approvals.</p>
                                </div>
                            </div>
                        </div>

                        {/* Assurance */}
                        <div>
                            <h3 className="text-2xl font-normal mb-8 text-white">Long-term Assurance</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Output Guarantee</h4>
                                    <p className="text-white">Linear performance warranty ensuring 90% capacity after 25 years of service.</p>
                                </div>
                                <div>
                                    <h4 className="text-lg font-normal mb-2 text-gray-400">Support Network</h4>
                                    <p className="text-white">Dedicated technical support and maintenance teams available locally.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Signup Form Section */}
            <section id="signup-form" className="py-24 px-6 bg-black">
                <div className="container mx-auto max-w-4xl">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl md:text-5xl font-normal mb-4 text-white">
                            Start Your Solar Journey
                        </h2>
                        <p className="text-xl text-gray-400">
                            Ready to take control of your energy future? Fill out the form below to speak with an Energy Consultant.
                        </p>
                    </div>


                    <form onSubmit={handleSubmit} className="space-y-6 bg-gray-900 rounded-lg p-8 md:p-12 border border-gray-800">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                    placeholder="John Doe"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                    placeholder="john@example.com"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Phone *</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                    className="w-full px-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                                    placeholder="+234 800 000 0000"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-2">Location *</label>
                                <div className="relative">
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 z-10" />
                                    <input
                                        ref={locationInputRef}
                                        type="text"
                                        name="location"
                                        value={formData.location}
                                        onChange={handleLocationChange}
                                        required
                                        autoComplete="off"
                                        className="w-full pl-10 pr-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent autocomplete-input"
                                        placeholder="Start typing your address (e.g., Ikeja, Lagos, Nigeria)"
                                        style={{ zIndex: 1 }}
                                    />
                                    {formData.location && locationSuggestions.length > 0 && (
                                        <div className="absolute left-0 right-0 mt-2 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl z-50 max-h-60 overflow-auto">
                                            {locationSuggestions.map((suggestion) => (
                                                <button
                                                    key={suggestion.place_id}
                                                    type="button"
                                                    onClick={() => handleLocationSelect(suggestion)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-800 transition-colors"
                                                >
                                                    <div className="text-white text-sm">
                                                        {suggestion.main_text || suggestion.description}
                                                    </div>
                                                    {suggestion.secondary_text && (
                                                        <div className="text-xs text-gray-400">{suggestion.secondary_text}</div>
                                                    )}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {isLocationLoading && (
                                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                                        </div>
                                    )}
                                    {locationError && (
                                        <p className="absolute left-0 right-0 mt-2 text-xs text-red-400">{locationError}</p>
                                    )}
                                </div>
                                {formData.country && (
                                    <p className="mt-2 text-xs text-gray-400">
                                        Detected: {formData.state && `${formData.state}, `}{formData.country}
                                        {formData.local_government_area && ` • ${formData.local_government_area}`}
                                    </p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">
                                    Select a location from the dropdown suggestions
                                </p>
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Property Type</label>
                            <select
                                name="propertyType"
                                value={formData.propertyType}
                                onChange={handleInputChange}
                                className="w-full px-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent"
                            >
                                <option value="">Select Property Type (Optional)</option>
                                <option value="residential">Residential</option>
                                <option value="commercial">Commercial</option>
                                <option value="industrial">Industrial</option>
                                <option value="agricultural">Agricultural</option>
                                <option value="other">Other</option>
                            </select>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-2">Message (Optional)</label>
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                                rows={4}
                                className="w-full px-4 py-3 bg-black border border-gray-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-white focus:border-transparent resize-none"
                                placeholder="Tell us about your energy needs..."
                            />
                        </div>

                        <div className="flex justify-center">
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-12 py-4 bg-white text-black font-medium rounded hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-lg flex items-center justify-center gap-2 min-w-[200px]"
                            >
                                {isSubmitting ? (
                                    <>
                                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
                                        Submitting...
                                    </>
                                ) : (
                                    <>
                                        Submit
                                        <ArrowRight className="w-5 h-5" />
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default Solar;
